import { forwardRef, useEffect, useState } from 'react';

import { styled } from '@spazious/dollhouse-theme';
import { useForkRef } from '@spazious/react-hooks';

import { HotkeyHint, useHotkeyHint } from '../HotkeyHint/index.js';

import { ButtonBase, ButtonBaseElement, ButtonBaseProps } from './ButtonBase.js';

export type ButtonHotkeyElement = ButtonBaseElement;
export interface ButtonHotkeyProps extends ButtonBaseProps {
  hotkey: string;
}

export const ButtonHotkey = styled(
  forwardRef<ButtonHotkeyElement, ButtonHotkeyProps>(({ hotkey, disabled, ...restProps }, ref) => {
    const [setElement, hintProps] = useHotkeyHint<ButtonHotkeyElement>();

    const [showShortCuts, setShowShortCuts] = useState(false);

    useEffect(() => {
      // https://stackoverflow.com/questions/65429466/best-way-to-listen-for-cmd-shift-z-and-cmd-z-using-javascript
      // https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/getModifierState
      const keyup = (e: KeyboardEvent) => {
        if (e.code === 'MetaLeft' || e.key === 'Control') {
          setShowShortCuts(false);
          window.removeEventListener('keyup', keyup);
        }
      };
      const keydown = (e: KeyboardEvent) => {
        if (e.code === 'MetaLeft' || e.ctrlKey) {
          setShowShortCuts(true);
          window.addEventListener('keyup', keyup);
        }
      };
      const visibilitychange = () => {
        setShowShortCuts(false);
      };

      window.addEventListener('keydown', keydown);
      document.addEventListener('visibilitychange', visibilitychange);

      return () => {
        window.removeEventListener('keydown', keydown);
        window.removeEventListener('keyup', keyup);
        document.removeEventListener('visibilitychange', visibilitychange);
      };
    }, []);

    return (
      <>
        <ButtonBase {...restProps} ref={useForkRef(setElement, ref)} disabled={disabled} />
        {!disabled && showShortCuts && <HotkeyHint binding={hotkey} {...hintProps} />}
      </>
    );
  })
).withConfig({
  displayName: 'ButtonHotkey',
})<ButtonHotkeyProps>``;
